import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Loader2, ExternalLink, CheckCircle, AlertCircle,
  Linkedin, Globe, Send, Link2, Wifi, WifiOff, Clock, X, Square, CheckSquare
} from 'lucide-react';

const API = '/api';

const FB_ICON = () => (
  <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const IG_ICON = () => (
  <svg className="h-5 w-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
);

export default function PublishPanel({ content, onPublished }) {
  const [platforms, setPlatforms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState({});
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [schedulePlatforms, setSchedulePlatforms] = useState([]);
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  // Pre-select platforms from content's target platforms
  useEffect(() => {
    if (content?.platforms?.length) {
      setSelected(new Set(content.platforms.map(p => p.toLowerCase())));
    }
  }, [content?.platforms]);

  const fetchPlatforms = async () => {
    try {
      const { data } = await axios.get(`${API}/social/publish/platforms`, { withCredentials: true });
      setPlatforms(data);
    } catch (e) {
      toast.error('Failed to load platform status');
    } finally {
      setLoading(false);
    }
  };

  const publications = content?.publications || [];
  const isPublished = (platform) => publications.some(p => p.platform === platform);

  const publishTo = async (platform, extra = {}) => {
    setPublishing(prev => ({ ...prev, [platform]: true }));
    try {
      const { data } = await axios.post(
        `${API}/social/publish/${platform}/${content.content_id}`,
        extra,
        { withCredentials: true }
      );
      toast.success(`Published to ${platform}!`);
      onPublished?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to publish to ${platform}`);
    } finally {
      setPublishing(prev => ({ ...prev, [platform]: false }));
    }
  };

  const publishToSelected = async () => {
    const selectedArr = [...selected].filter(p => !isPublished(p));
    if (selectedArr.length === 0) {
      toast.error('No unpublished platforms selected');
      return;
    }
    setBulkPublishing(true);
    try {
      const { data } = await axios.post(
        `${API}/social/publish/multi/${content.content_id}`,
        { platforms: selectedArr },
        { withCredentials: true }
      );
      const { summary, results } = data;
      if (summary.published > 0) {
        toast.success(`Published to ${summary.published} platform(s)!`);
      }
      if (summary.failed > 0) {
        const failedNames = Object.entries(results)
          .filter(([, r]) => !r.success)
          .map(([name, r]) => `${name}: ${r.error?.slice(0, 60)}`)
          .join('; ');
        toast.error(`Failed: ${failedNames}`);
      }
      onPublished?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk publish failed');
    } finally {
      setBulkPublishing(false);
    }
  };

  const testConnections = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/social/publish/test-connections`, {}, { withCredentials: true });
      setTestResults(data);
      const ready = data.summary?.ready_to_publish || 0;
      if (ready > 0) {
        toast.success(`All ${ready} platforms ready to publish!`);
      } else {
        toast.warning('No platforms are currently connected');
      }
    } catch (e) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const toggle = (platform) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(platform) ? next.delete(platform) : next.add(platform);
      return next;
    });
  };

  if (loading) {
    return (
      <Card className="border-slate-200 border-l-4 border-l-emerald-500">
        <CardContent className="flex items-center justify-center py-8 text-slate-400 text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading platforms...
        </CardContent>
      </Card>
    );
  }

  if (!platforms) return null;

  const isBlog = content?.content_type === 'blog';
  const isImage = content?.content_type === 'image';
  const isVideo = content?.content_type === 'video';
  const firstMetaPage = platforms.meta?.pages?.[0];

  // Build available platforms list
  const availablePlatforms = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <Linkedin className="h-5 w-5 text-blue-700" />,
      connected: platforms.linkedin?.connected,
      detail: platforms.linkedin?.user_name || '',
      show: true,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <FB_ICON />,
      connected: !!firstMetaPage,
      detail: firstMetaPage?.page_name || '',
      show: true,
      publishExtra: { page_id: firstMetaPage?.page_id },
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <IG_ICON />,
      connected: !!firstMetaPage?.ig_user_id,
      detail: firstMetaPage?.ig_user_id ? 'Business Account' : '',
      show: isImage || isVideo,
      publishExtra: { page_id: firstMetaPage?.page_id },
    },
    {
      id: 'wordpress',
      name: 'WordPress',
      icon: <Globe className="h-5 w-5 text-sky-700" />,
      connected: platforms.wordpress?.connected,
      detail: platforms.wordpress?.site_url?.replace('https://', '') || '',
      show: isBlog,
    },
  ];

  const visiblePlatforms = availablePlatforms.filter(p => p.show);
  const selectedUnpublished = [...selected].filter(p => !isPublished(p) && visiblePlatforms.some(vp => vp.id === p && vp.connected));

  return (
    <Card className="border-slate-200 border-l-4 border-l-emerald-500" data-testid="publish-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-600" /> Publish to Platforms
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={testConnections}
            disabled={testing}
            className="h-7 text-xs"
            data-testid="test-connections-btn"
          >
            {testing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wifi className="h-3 w-3 mr-1" />}
            {testing ? 'Testing...' : 'Test Connections'}
          </Button>
        </div>
        <p className="text-xs text-slate-500">Select platforms and publish. Content targets: <span className="font-medium">{content?.platforms?.join(', ') || 'all'}</span></p>

        {/* Test Results */}
        {testResults && (
          <div className="mt-2 space-y-1.5 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid="test-results">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600">Connection Status</span>
              <Badge variant="outline" className="text-[10px]">
                {testResults.summary.ready_to_publish}/{testResults.summary.total_platforms} ready
              </Badge>
            </div>
            {Object.entries(testResults.platforms).map(([platform, result]) => (
              <div key={platform} className="flex items-center justify-between text-xs">
                <span className="capitalize font-medium text-slate-600">{platform}</span>
                <div className="flex items-center gap-1.5">
                  {result.can_post ? (
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  ) : (
                    <WifiOff className="h-3 w-3 text-red-400" />
                  )}
                  <span className={result.can_post ? 'text-emerald-600' : 'text-red-500'}>
                    {result.message.slice(0, 50)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Platform Selection Rows */}
        {visiblePlatforms.map(plat => {
          const published = isPublished(plat.id);
          const checked = selected.has(plat.id);

          return (
            <div key={plat.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                published ? 'bg-emerald-50 border-emerald-200' :
                checked ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'
              }`}
              onClick={() => !published && plat.connected && toggle(plat.id)}
              data-testid={`publish-${plat.id}-row`}
            >
              <div className="flex items-center gap-3">
                {/* Checkbox */}
                {published ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />
                ) : plat.connected ? (
                  <button
                    onClick={e => { e.stopPropagation(); toggle(plat.id); }}
                    className="shrink-0"
                    data-testid={`select-${plat.id}`}
                  >
                    {checked ? (
                      <CheckSquare className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Square className="h-5 w-5 text-slate-300" />
                    )}
                  </button>
                ) : (
                  <Square className="h-5 w-5 text-slate-200 shrink-0" />
                )}

                {/* Platform info */}
                <div className="flex items-center gap-2">
                  {plat.icon}
                  <span className="text-sm font-medium">{plat.name}</span>
                  {published ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Published</Badge>
                  ) : plat.connected ? (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200 text-[10px]">{plat.detail || 'Connected'}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-400 text-[10px]">Not connected</Badge>
                  )}
                </div>
              </div>

              {/* Right-side actions */}
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                {published && publications.find(p => p.platform === plat.id)?.wp_link && (
                  <a href={publications.find(p => p.platform === plat.id).wp_link} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 text-blue-500 hover:text-blue-700" />
                  </a>
                )}
                {!published && plat.connected && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-slate-500"
                    onClick={() => publishTo(plat.id, plat.publishExtra || {})}
                    disabled={publishing[plat.id]}
                    data-testid={`publish-${plat.id}-btn`}
                  >
                    {publishing[plat.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                    Publish
                  </Button>
                )}
                {!published && !plat.connected && (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => window.location.href = '/social/connections'}
                  >
                    <Link2 className="h-3 w-3 mr-1" /> Set Up
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Bulk Publish Button */}
        {selectedUnpublished.length > 0 && (
          <Button
            className="w-full"
            onClick={publishToSelected}
            disabled={bulkPublishing}
            data-testid="publish-selected-btn"
          >
            {bulkPublishing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Publish to {selectedUnpublished.length} Selected Platform{selectedUnpublished.length > 1 ? 's' : ''}
          </Button>
        )}

        {/* Auto-Schedule Section */}
        {content?.auto_publish_status === 'scheduled' ? (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="schedule-active">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Scheduled</span>
                <span className="text-xs text-amber-600">
                  {new Date(content.scheduled_at).toLocaleDateString()} at {new Date(content.scheduled_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                </span>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200"
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/social/schedule/${content.content_id}`, { withCredentials: true });
                    toast.success('Schedule cancelled');
                    onPublished?.();
                  } catch (e) { toast.error('Failed to cancel'); }
                }}
                data-testid="cancel-schedule-btn"
              >
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
            <p className="text-[10px] text-amber-600 mt-1">
              Platforms: {(content.auto_publish_platforms || []).join(', ')}
            </p>
          </div>
        ) : content?.auto_publish_status === 'published' ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="auto-published-badge">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Auto-published</span>
              <span className="text-xs text-emerald-600">{content.auto_published_at?.split('T')[0]}</span>
            </div>
          </div>
        ) : !showSchedule ? (
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => {
              setShowSchedule(true);
              setSchedulePlatforms([...selected]);
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              setScheduleDate(tomorrow.toISOString().split('T')[0]);
            }}
            data-testid="show-schedule-btn"
          >
            <Clock className="h-3.5 w-3.5 mr-1.5" /> Schedule Auto-Publish
          </Button>
        ) : (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="schedule-form">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">Schedule Auto-Publish</span>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setShowSchedule(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-medium">Date</label>
                <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-md" data-testid="schedule-date-input" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-medium">Time</label>
                <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-md" data-testid="schedule-time-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-medium">Platforms</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {visiblePlatforms.filter(p => p.connected).map(p => (
                  <button key={p.id} type="button"
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      schedulePlatforms.includes(p.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200'
                    }`}
                    onClick={() => setSchedulePlatforms(prev =>
                      prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                    )}
                    data-testid={`schedule-platform-${p.id}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <Button size="sm" className="w-full" disabled={scheduling || !scheduleDate || schedulePlatforms.length === 0}
              onClick={async () => {
                setScheduling(true);
                try {
                  const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
                  await axios.post(`${API}/social/schedule/${content.content_id}`, {
                    scheduled_at: scheduledAt, platforms: schedulePlatforms
                  }, { withCredentials: true });
                  toast.success(`Scheduled for ${scheduleDate} at ${scheduleTime}`);
                  setShowSchedule(false);
                  onPublished?.();
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to schedule'); }
                finally { setScheduling(false); }
              }}
              data-testid="confirm-schedule-btn"
            >
              {scheduling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Clock className="h-3.5 w-3.5 mr-1.5" />}
              Confirm Schedule
            </Button>
          </div>
        )}

        {/* Publication History */}
        {publications.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs font-medium text-slate-400 mb-2">Publication History</p>
            <div className="space-y-1">
              {publications.map((pub, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-slate-500 px-2 py-1 bg-slate-50 rounded">
                  <span className="capitalize font-medium">{pub.platform}</span>
                  <span>{pub.published_at?.split('T')[0]}</span>
                  {pub.wp_link && (
                    <a href={pub.wp_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
